import { useEffect } from "react";

/* ------------------------------------------------------------------ */
/* Sizes every list-table scroll box to the space left in the page     */
/* scroller below it, so the rows scroll inside the table while the    */
/* page title, filters and buttons above stay where they are.          */
/*                                                                     */
/* Writes --tbl-fit-h on the box; TableTheme.css uses it as the        */
/* max-height (falling back to its own vh-based value). Below the      */
/* tablet breakpoint the variable is removed: phones keep the CSS      */
/* fallback and normal page scrolling so nothing is squeezed.          */
/* ------------------------------------------------------------------ */

const OUTER_SELECTOR = ".tab-radio .data.table-responsive, .pending-table";
const MIN_HEIGHT = 320; // never smaller than this, whatever sits above
const BELOW_SPACE = 76; // pagination / "no data" row rendered after the table
const MIN_VIEWPORT_WIDTH = 768;

const scrollBoxOf = (outer) => {
  const inner = Array.from(outer.children).find((el) =>
    el.classList.contains("table-responsive")
  );
  if (inner) return inner;
  // tables rendered without react-bootstrap's wrapper scroll in the outer div
  return outer.classList.contains("data") ? outer : null;
};

const fitTables = (root) => {
  const enabled = window.innerWidth >= MIN_VIEWPORT_WIDTH;
  const scroller = root.closest(".tab-radio") || root;
  const scrollerRect = scroller.getBoundingClientRect();
  const viewportBottom = Math.min(scrollerRect.bottom, window.innerHeight);

  root.querySelectorAll(OUTER_SELECTOR).forEach((outer) => {
    const box = scrollBoxOf(outer);
    if (!box) return;
    if (!enabled || box.offsetParent === null) {
      box.style.removeProperty("--tbl-fit-h");
      return;
    }
    // distance from the top of the scroller's content, so the result does
    // not change as the page itself is scrolled
    const top =
      box.getBoundingClientRect().top - scrollerRect.top + scroller.scrollTop;
    const available = viewportBottom - scrollerRect.top - top - BELOW_SPACE;
    const height = Math.max(MIN_HEIGHT, Math.floor(available));
    if (box.style.getPropertyValue("--tbl-fit-h") !== `${height}px`) {
      box.style.setProperty("--tbl-fit-h", `${height}px`);
    }
  });
};

/**
 * @param {React.RefObject<HTMLElement>} rootRef  element that contains the routes
 * @param {any} routeKey  changes on navigation (e.g. location.pathname)
 */
const useFitTableHeight = (rootRef, routeKey) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let frame = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        fitTables(root);
      });
    };

    schedule();
    window.addEventListener("resize", schedule);

    // filters, tabs and data load after the route renders, and tab panes
    // switch by class; re-measure on those changes (one pass per frame)
    const observer = new MutationObserver(schedule);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [rootRef, routeKey]);
};

export default useFitTableHeight;
